import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Gantt as GanttAtom } from 'components/atoms/gantt';
import { PageLoading } from 'components/atoms/loading';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { ProjectTemplateProjectPhase } from 'data/templates/projects';
import { queueMacrotask } from 'utils/macrotask';
import { ProjectTemplateToolBar } from '../commons';
import { useTemplateGanttDataConfig } from '../gantt/config/use-data-config';
import { useTemplateGanttStaticConfig } from '../gantt/config/use-static-config';
import { useGanttRef } from '../gantt/use-gantt-ref';
import { useTableActions } from '../plan/table/actions';

const Gantt = styled(GanttAtom).attrs({ splitter: false })`
  margin-left: var(--layout-02);

  .b-sch-header-row-0 {
    display: none;
  }

  .b-date-cell,
  .b-number-cell {
    padding: 0 calc(var(--layout-02) + var(--spacing-01));
  }
`;

export const ProjectTemplatePreviewGantt: FC<{
  phases?: ProjectTemplateProjectPhase[] | null;
}> = ({ phases }) => {
  const { mode, expandCollapse } = useTableActions();
  const [showLoader, setShowLoader] = useState(true);
  const { ganttRef, setGanttRef, collapseAll, expandAll } =
    useGanttRef(showLoader);

  const _staticConfig = useTemplateGanttStaticConfig({ ganttRef });
  /**
   * making gantt as readonly.
   * taskDrag is enabled though gantt is marked as readonly
   * hence disabling drag explicitly.
   */
  const staticConfig = {
    ..._staticConfig,
    readOnly: true,
    taskDragFeature: {
      ...(_staticConfig.taskDragFeature || {}),
      disabled: true,
    },
  };
  const dataConfig = useTemplateGanttDataConfig(phases);

  /**
   * Forcefully showing loader for zero timeout
   * for faster tab switching in project detail.
   * else, user stays on project plan until gantt loads.
   */
  useEffect(() => {
    queueMacrotask(() => {
      setShowLoader(false);
    });
  }, []);

  useEffect(() => {
    switch (mode) {
      case 'collapse':
        collapseAll();
        break;
      case 'expand':
      default:
        expandAll();
        break;
    }
  }, [collapseAll, expandAll, mode]);

  return (
    <>
      <ProjectTemplateToolBar
        hideTabBar
        mode={mode}
        expandCollapse={expandCollapse}
      />
      <ErrorBoundary fallback={() => <>Something went wrong</>}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {!showLoader ? (
          <Gantt {...staticConfig} {...dataConfig} ganttRef={setGanttRef} />
        ) : (
          <PageLoading css />
        )}
      </ErrorBoundary>
    </>
  );
};
